import { useState, useRef } from 'react';
import NewsletterSponsorBanner from './NewsletterSponsorBanner';

const MAILCHIMP_ACTION =
  'https://roseville.us1.list-manage.com/subscribe/post?u=38f82355d91d7132967a6d4b6&id=fc814891ad&f_id=00782fe1f0';

export default function MailchimpNewsletter() {
  const [formData, setFormData] = useState({
    EMAIL: '',
    FNAME: '',
    LNAME: '',
    PHONE: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle'
  );
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): boolean => {
    if (
      !formData.EMAIL ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.EMAIL)
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('EMAIL', formData.EMAIL);
      if (formData.FNAME) body.append('FNAME', formData.FNAME);
      if (formData.LNAME) body.append('LNAME', formData.LNAME);
      if (formData.PHONE) body.append('PHONE', formData.PHONE);
      // Honeypot field – must be empty
      body.append('b_38f82355d91d7132967a6d4b6_fc814891ad', '');

      const response = await fetch(MAILCHIMP_ACTION, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      // With `no-cors` we cannot read the response, but we can at least
      // assume success if the request didn't throw.
      if (!response.ok && response.type !== 'opaque') {
        throw new Error('Network response was not ok');
      }

      setStatus('success');
      setFormData({ EMAIL: '', FNAME: '', LNAME: '', PHONE: '' });
    } catch (err) {
      console.error('Mailchimp subscription error:', err);
      setStatus('error');
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-800">
      <div className="max-w-4xl mx-auto px-6">
        {/* Newsletter Sponsor Banner */}
        <NewsletterSponsorBanner />

        {/* Newsletter Signup */}
        <div className="bg-white rounded-2xl p-10 shadow-xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-teal-700 to-teal-900 p-12">
            <div className="absolute top-0 right-0 w-72 h-72 bg-teal-600/20 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-amber-500/10 rounded-full translate-y-1/2" />

            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-10">
              {/* Copy */}
              <div className="text-white max-w-lg flex-shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-send-line text-amber-400 text-xl" />
                  </span>
                  <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">
                    Stay Connected
                  </span>
                </div>
                <h2 className="text-3xl font-bold mb-3">
                  Get the Best of Roseville in Your Inbox
                </h2>
                <p className="text-white/80 text-sm leading-relaxed">
                  Subscribe to our newsletter for weekly updates on local events,
                  exclusive deals, community news, and insider tips about Roseville,
                  California.
                </p>
              </div>

              {/* Form */}
              <div className="w-full max-w-md">
                {status === 'success' ? (
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                    <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-check-line text-white text-2xl" />
                    </div>
                    <p className="text-white font-semibold">
                      You&apos;re subscribed!
                    </p>
                    <p className="text-white/70 text-sm mt-1">
                      Check your inbox to confirm your subscription.
                    </p>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    id="mc-embedded-subscribe-form"
                    name="mc-embedded-subscribe-form"
                    data-readdy-form="" // Fixed invalid JSX attribute
                    action={MAILCHIMP_ACTION}
                    method="POST"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-3"
                  >
                    {/* First Name & Last Name row */}
                    <div className="flex gap-3">
                      <input
                        type="text"
                        name="FNAME"
                        id="mce-FNAME"
                        placeholder="First Name"
                        value={formData.FNAME}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none bg-white rounded-xl"
                      />
                      <input
                        type="text"
                        name="LNAME"
                        id="mce-LNAME"
                        placeholder="Last Name"
                        value={formData.LNAME}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none bg-white rounded-xl"
                      />
                    </div>

                    {/* Email */}
                    <input
                      type="email"
                      name="EMAIL"
                      id="mce-EMAIL"
                      placeholder="Email Address *"
                      value={formData.EMAIL}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none bg-white rounded-xl"
                    />

                    {/* Phone */}
                    <input
                      type="text"
                      name="PHONE"
                      id="mce-PHONE"
                      placeholder="Phone Number"
                      value={formData.PHONE}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none bg-white rounded-xl"
                    />

                    {/* Honeypot – prevents bot signups */}
                    <div
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-5000px' }}
                    >
                      <input
                        type="text"
                        name="b_38f82355d91d7132967a6d4b6_fc814891ad"
                        tabIndex={-1}
                        defaultValue=""
                      />
                    </div>

                    {/* Status messages (kept for potential future use) */}
                    <div id="mce-responses">
                      <div id="mce-error-response" style={{ display: 'none' }} />
                      <div id="mce-success-response" style={{ display: 'none' }} />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      name="subscribe"
                      id="mc-embedded-subscribe"
                      disabled={status === 'loading'}
                      className="whitespace-nowrap w-full px-6 py-3.5 bg-amber-500 text-white text-sm font-semibold rounded-xl hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-60"
                    >
                      {status === 'loading' ? 'Subscribing...' : 'Subscribe Now'}
                    </button>

                    {status === 'error' && (
                      <p className="text-red-300 text-xs">
                        Something went wrong. Please try again.
                      </p>
                    )}
                    <p className="text-white/50 text-xs text-center">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
